import { useState, useEffect } from 'react';

interface PersistedStateOptions<T> {
  storageKey: string;
  defaultValue: T;
  storage?: Storage; // allows for different storage types (sessionStorage/localStorage)
  validate?: (value: unknown) => value is T;
}

export function usePersistedState<T>({
  storageKey,
  defaultValue,
  storage = sessionStorage,
  validate
}: PersistedStateOptions<T>) {
  const getInitialState = (): T => {
    try {
      const savedState = storage.getItem(storageKey);
      if (savedState) {
        const parsed = JSON.parse(savedState);
        if (!validate || validate(parsed)) return parsed;
      }
    } catch { /* Fall back to a usable form if saved state is unreadable. */ }
    return defaultValue;
  };

  const [state, setState] = useState<T>(getInitialState);

  // Save state on changes
  useEffect(() => {
    try {
      storage.setItem(storageKey, JSON.stringify(state));
    } catch { /* Keep in-memory state when storage is unavailable. */ }
  }, [state, storageKey, storage]);

  return [state, setState] as const;
}
